import { useState, useEffect, useMemo } from "react";
import { Link } from "react-router-dom";
import { useSelector } from "react-redux";
import {
  Container,
  Typography,
  Box,
  Grid,
  Card,
  CardContent,
  CardMedia,
  Button,
  Paper,
} from "@mui/material";
import PageLayout from "../components/Common/PageLayout";
import PageBanner from "../components/Common/PageBanner";
import ReadMore from "../components/Common/ReadMore";
import { some } from "lodash";

/**
 *
 * @returns {JSX.Element}
 * @component
 */
const AboutUs = () => {
  const [loader, setLoader] = useState(true);

  // Extract state data with safe access patterns
  // eslint-disable-next-line no-unused-vars
  const {
    about: { about: aboutData },
  } = useSelector((state) => state.pageContents) || {};
  //const formToken = auth?.user?.formToken;

  const aboutMissionSections = [
    "section_list_first",
    "section_list_second",
    "section_list_third",
    "section_list_four",
    "section_list_five",
    "section_list_six",
    "section_list_seven",
  ];

  const directorSocialSections = [
    "director_facebook_link",
    "director_twitter_link",
    "director_pinterest_link",
    "director_instagram_link",
  ];

  // Process directors data for grid layout
  const directorsInGroups = useMemo(() => {
    if (!aboutData?.directorsdata?.length) {
      return [];
    }

    const result = [];
    let tempGroup = [];
    const groupSize = 3;

    aboutData.directorsdata.forEach((director, index) => {
      tempGroup.push(director);

      if (
        tempGroup.length === groupSize ||
        index === aboutData.directorsdata.length - 1
      ) {
        result.push([...tempGroup]);
        tempGroup = [];
      }
    });

    return result;
  }, [aboutData?.directorsdata]);

  // Initialize loading state
  useEffect(() => {
    const timer = setTimeout(() => setLoader(false), 1500);
    return () => clearTimeout(timer);
  }, []);

  return (
    <PageLayout pageName="About Us" showLoader={loader} checkComingSoon>
      {/* Banner */}
      {aboutData?.aboutbanner ? (
        <PageBanner
          bannerImage={aboutData.aboutbanner.banner_image}
          mainTitle={aboutData.aboutbanner.banner_title || "About Us"}
          pageTitle="About"
        />
      ) : (
        <PageBanner mainTitle="About Us" pageTitle="About" />
      )}

      {/* About Area */}
      <Box component="section" sx={{ py: 8 }}>
        <Container>
          {/* Who We Are Section */}
          {aboutData?.aboutWho && (
            <>
              <Typography
                variant="h4"
                component="h1"
                align="center"
                gutterBottom
                sx={{ mb: 4 }}
              >
                {aboutData.aboutWho.section_title}
              </Typography>

              <Grid container spacing={4} sx={{ mb: 8 }} alignItems="center">
                <Grid size={{ xs: 12, md: 6 }}>
                  <Box
                    component="img"
                    src={aboutData.aboutWho.section_image}
                    alt="About Us"
                    sx={{
                      width: "100%",
                      borderRadius: 2,
                      boxShadow: 3,
                    }}
                  />
                </Grid>
                <Grid size={{ xs: 12, md: 6 }}>
                  <Typography variant="h5" gutterBottom>
                    {aboutData.aboutWho.section_sub_title}
                  </Typography>
                  <Typography variant="body1">
                    <div
                      dangerouslySetInnerHTML={{
                        __html: aboutData.aboutWho.section_description,
                      }}
                    />
                  </Typography>
                </Grid>
              </Grid>
            </>
          )}

          {/* Membership Section */}
          {aboutData?.aboutMember && (
            <Paper elevation={0} sx={{ bgcolor: "background.paper", mb: 8 }}>
              <Grid container>
                <Grid size={{ xs: 12, md: 6 }}>
                  <Box sx={{ p: 4 }}>
                    <Typography variant="h5" gutterBottom>
                      {aboutData.aboutMember.section_title}
                    </Typography>
                    <Typography variant="body1" sx={{ whiteSpace: "pre-line" }}>
                      <div
                        dangerouslySetInnerHTML={{
                          __html: aboutData.aboutMember.section_description,
                        }}
                      />
                    </Typography>
                    <Button
                      component={Link}
                      to="/signup"
                      variant="contained"
                      color="primary"
                      sx={{ mt: 2 }}
                    >
                      Join Now
                    </Button>
                  </Box>
                </Grid>
                <Grid size={{ xs: 12, md: 6 }}>
                  <Box
                    sx={{
                      height: "100%",
                      minHeight: 300,
                      backgroundImage: `url('${aboutData.aboutMember.section_image}')`,
                      backgroundSize: "cover",
                      backgroundPosition: "center",
                    }}
                  />
                </Grid>
              </Grid>
            </Paper>
          )}

          {/* Mission & Vision Section */}
          {aboutData?.aboutMission && (
            <Box
              sx={{ bgcolor: "#f9fbfc", py: 6, px: 2, borderRadius: 2, mb: 8 }}
            >
              <Grid container spacing={4} alignItems="center">
                <Grid size={{ xs: 12, md: 6 }}>
                  <Typography variant="h5" gutterBottom>
                    {aboutData.aboutMission.section_title}
                  </Typography>

                  {aboutData.aboutMission.section_description && (
                    <Typography variant="body1">
                      <div
                        dangerouslySetInnerHTML={{
                          __html: aboutData.aboutMission.section_description,
                        }}
                      />
                    </Typography>
                  )}

                  <Typography
                    variant="subtitle1"
                    fontWeight="bold"
                    gutterBottom
                  >
                    {aboutData.aboutMission.section_sub_title}
                  </Typography>

                  {/* Mission List Items */}
                  {some(aboutData.aboutMission, (v, k) =>
                    aboutMissionSections.includes(k),
                  ) && (
                    <Box component="ul" sx={{ pl: 2 }} key={`mission-section`}>
                      {aboutMissionSections.map((section, index) => {
                        if (aboutData.aboutMission[section]) {
                          return (
                            <Box
                              key={`mission-section-${index}`}
                              component="li"
                              sx={{
                                mb: 1,
                                display: "flex",
                                alignItems: "center",
                              }}
                            >
                              <Box
                                component="i"
                                className="flaticon-tick"
                                sx={{ mr: 1, color: "primary.main" }}
                              />
                              <Typography variant="body1">
                                {aboutData.aboutMission[section]}
                              </Typography>
                            </Box>
                          );
                        }
                        return null;
                      })}
                    </Box>
                  )}
                </Grid>
                <Grid size={{ xs: 12, md: 6 }}>
                  <Box
                    component="img"
                    src={aboutData.aboutMission.section_image}
                    alt="Mission"
                    sx={{ width: "100%", borderRadius: 2 }}
                  />
                </Grid>
              </Grid>
            </Box>
          )}

          {/* Board of Directors Section */}
          {aboutData?.aboutdirectors?.section_title &&
            aboutData?.directorsdata?.length > 0 && (
              <Box sx={{ mb: 8 }}>
                <Typography variant="h4" align="center" gutterBottom>
                  {aboutData.aboutdirectors.section_title}
                </Typography>
                <Typography variant="body1" align="center" sx={{ mb: 4 }}>
                  <div
                    dangerouslySetInnerHTML={{
                      __html: aboutData.aboutdirectors.section_description,
                    }}
                  />
                </Typography>

                {directorsInGroups.map((group, groupIndex) => (
                  <Grid
                    container
                    spacing={3}
                    key={`group-${groupIndex}`}
                    sx={{ mb: 4 }}
                  >
                    {group.map((director, index) => (
                      <Grid
                        size={{ xs: 12, md: 4 }}
                        key={`director-${groupIndex}-${index}`}
                      >
                        <Card elevation={2} sx={{ height: "100%" }}>
                          <CardMedia
                            component="img"
                            height="240"
                            image={director.director_image}
                            alt={director.director_name}
                          />
                          <CardContent>
                            <Typography variant="h6" gutterBottom>
                              {director.director_name}
                            </Typography>
                            <Typography
                              variant="subtitle2"
                              color="text.secondary"
                              gutterBottom
                            >
                              {director.director_specialization}
                            </Typography>
                            <ReadMore>{director.director_short_desc}</ReadMore>

                            {/* Social Links */}
                            {some(director, (v, k) =>
                              directorSocialSections.includes(k),
                            ) && (
                              <Box sx={{ mt: 2, display: "flex", gap: 1 }}>
                                {directorSocialSections.map(
                                  (section, index) => {
                                    if (director[section]) {
                                      return (
                                        <Link
                                          key={`director-social-${index}`}
                                          to={director[section]}
                                          target="_blank"
                                          rel="noreferrer"
                                        >
                                          <i
                                            className={`bx bxl-${section.split("_")[1]}`}
                                          ></i>
                                        </Link>
                                      );
                                    }
                                    return null;
                                  },
                                )}
                              </Box>
                            )}
                          </CardContent>
                        </Card>
                      </Grid>
                    ))}
                  </Grid>
                ))}
              </Box>
            )}

          {/* Membership CTA Section */}
          <Box alignItems="center" sx={{ width: "100%" }}>
            <Button
              component={Link}
              variant="contained"
              color="primary"
              size="large"
              to={"/signup"}
            >
              Become a Member
            </Button>
          </Box>
        </Container>
      </Box>
    </PageLayout>
  );
};

export default AboutUs;
